import React, { useState } from 'react';
import { Button, Callout, Divider, Icon } from '../ui';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import LazyStripeProvider from '../common/LazyStripeProvider';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';

interface AddPaymentMethodFormProps {
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
  onClose: () => void;
  existingMethodsCount: number;
}

interface PaymentMethodFormData {
  type: 'card' | 'bank';
  name: string;
  bankAccount?: {
    accountNumber: string;
    routingNumber: string;
    accountType: 'checking' | 'savings';
  };
  setAsDefault: boolean;
}

// ─── Card sub-form (needs Stripe context) ────────────────────────────────────
const CardPaymentForm: React.FC<{
  formData: PaymentMethodFormData;
  setFormData: (data: PaymentMethodFormData) => void;
  onSubmit: (cardElement: any) => Promise<void>;
  processing: boolean;
}> = ({ formData, setFormData, onSubmit, processing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement('card');
    if (!cardElement) { setCardError('Card element not found. Please refresh.'); return; }
    await onSubmit(cardElement);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Icon name="card" size={16} style={{ color: 'var(--brand-blue)' }} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Add Credit or Debit Card</h3>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
          Cardholder Name <span style={{ color: 'var(--err)' }}>*</span>
        </label>
        <input
          className="input"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Name on card"
          required
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
          Card Information
        </label>
        <div
          style={{
            padding: '12px 14px',
            border: `2px solid ${focused ? 'var(--brand-blue)' : cardError ? 'var(--err)' : 'var(--line)'}`,
            borderRadius: 8,
            background: cardError ? 'rgba(239,68,68,0.04)' : 'var(--bg-elev)',
            transition: 'border-color 0.15s',
          }}
        >
          <CardElement
            options={{
              style: {
                base: { fontSize: '15px', fontFamily: 'inherit', color: 'var(--ink)', '::placeholder': { color: 'var(--ink-4)' } },
                invalid: { color: 'var(--err)' },
              },
              hidePostalCode: true,
            }}
            onChange={(e) => { setCardComplete(!!e.complete); setCardError(e.error?.message ?? null); }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
        {cardError && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{cardError}</p>}
        {cardComplete && !cardError && (
          <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--ok)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="checkCircle" size={12} /> Card information is complete
          </p>
        )}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, cursor: 'pointer', fontSize: 14 }}>
        <input
          type="checkbox"
          checked={formData.setAsDefault}
          onChange={(e) => setFormData({ ...formData, setAsDefault: e.target.checked })}
        />
        Set as default payment method
      </label>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'var(--bg)', borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
        <Icon name="shield" size={14} style={{ color: 'var(--ok)', flexShrink: 0 }} />
        <span className="muted">Your card information is encrypted and secure. We use Stripe for payment processing.</span>
      </div>

      <Button
        variant="primary"
        block
        size="lg"
        onClick={handleSubmit}
        disabled={!cardComplete || !formData.name.trim() || processing || !stripe}
      >
        {processing ? 'Adding Card...' : 'Add Card Securely'}
      </Button>
    </div>
  );
};

// ─── Bank sub-form ────────────────────────────────────────────────────────────
const BankAccountForm: React.FC<{
  formData: PaymentMethodFormData;
  setFormData: (data: PaymentMethodFormData) => void;
  onSubmit: () => Promise<void>;
  processing: boolean;
}> = ({ formData, setFormData, onSubmit, processing }) => {
  const update = (field: string, value: string) =>
    setFormData({ ...formData, bankAccount: { ...formData.bankAccount!, [field]: value } });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Icon name="building" size={16} style={{ color: 'var(--brand-blue)' }} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Add Bank Account</h3>
      </div>

      <Callout tone="info" style={{ marginBottom: 16, fontSize: 13 }}>
        Bank account verification may take 1-2 business days. Micro-deposits will be made for verification.
      </Callout>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
            Account Holder Name <span style={{ color: 'var(--err)' }}>*</span>
          </label>
          <input className="input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Routing Number <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input className="input" value={formData.bankAccount?.routingNumber || ''} onChange={(e) => update('routingNumber', e.target.value)} maxLength={9} required />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Account Number <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input className="input" value={formData.bankAccount?.accountNumber || ''} onChange={(e) => update('accountNumber', e.target.value)} required />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>Account Type</label>
          <select className="input" value={formData.bankAccount?.accountType || 'checking'} onChange={(e) => update('accountType', e.target.value)}>
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
          </select>
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 14 }}>
        <input type="checkbox" checked={formData.setAsDefault} onChange={(e) => setFormData({ ...formData, setAsDefault: e.target.checked })} />
        Set as default payment method
      </label>

      <Button
        variant="primary"
        block
        size="lg"
        onClick={onSubmit}
        disabled={!formData.name.trim() || !formData.bankAccount?.routingNumber || !formData.bankAccount?.accountNumber || processing}
      >
        {processing ? 'Adding Account...' : 'Add Bank Account'}
      </Button>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = ({
  onSuccess,
  onError,
  onClose,
  existingMethodsCount,
}) => {
  const { createPaymentMethod } = usePaymentMethods();
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState<PaymentMethodFormData>({
    type: 'card',
    name: '',
    bankAccount: { accountNumber: '', routingNumber: '', accountType: 'checking' },
    setAsDefault: existingMethodsCount === 0,
  });

  const handleCardSubmit = async (cardElement: any) => {
    setProcessing(true);
    try {
      await createPaymentMethod({
        paymentMethodData: { type: 'card', card: cardElement, billing_details: { name: formData.name } },
        name: formData.name,
      });
      onSuccess('Credit card added successfully!');
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to add credit card');
    } finally {
      setProcessing(false);
    }
  };

  const handleBankSubmit = async () => {
    setProcessing(true);
    try {
      onError('Bank account functionality is not yet available. Please use a credit card instead.');
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to add bank account');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      {/* Type selector */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 10 }}>Choose Payment Method Type</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {(['card', 'bank'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setFormData({ ...formData, type })}
              style={{
                padding: 20,
                border: `2px solid ${formData.type === type ? 'var(--brand-blue)' : 'var(--line)'}`,
                borderRadius: 10,
                cursor: 'pointer',
                background: formData.type === type ? 'rgba(59,130,246,0.06)' : 'var(--bg-elev)',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              <Icon
                name={type === 'card' ? 'card' : 'building'}
                size={36}
                style={{ color: formData.type === type ? 'var(--brand-blue)' : 'var(--ink-3)', marginBottom: 8 }}
              />
              <div style={{ fontWeight: formData.type === type ? 600 : 400, fontSize: 14, color: formData.type === type ? 'var(--brand-blue)' : 'var(--ink)' }}>
                {type === 'card' ? 'Credit/Debit Card' : 'Bank Account'}
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                {type === 'card' ? 'Instant processing' : 'Lower fees'}
              </div>
            </button>
          ))}
        </div>
      </div>

      <Divider style={{ marginBottom: 20 }} />

      {formData.type === 'card' ? (
        <LazyStripeProvider>
          <CardPaymentForm formData={formData} setFormData={setFormData} onSubmit={handleCardSubmit} processing={processing} />
        </LazyStripeProvider>
      ) : (
        <BankAccountForm formData={formData} setFormData={setFormData} onSubmit={handleBankSubmit} processing={processing} />
      )}

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
        <Button variant="ghost" onClick={onClose} disabled={processing}>Cancel</Button>
      </div>
    </div>
  );
};

export default AddPaymentMethodForm;
